package org.maplibre.compose.layers

import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.dp
import kotlinx.serialization.json.JsonPrimitive
import org.maplibre.compose.expressions.ast.Expression
import org.maplibre.compose.expressions.dsl.const
import org.maplibre.compose.expressions.value.BooleanValue
import org.maplibre.compose.expressions.value.CirclePitchAlignment
import org.maplibre.compose.expressions.value.CirclePitchScale
import org.maplibre.compose.expressions.value.ColorValue
import org.maplibre.compose.expressions.value.DpOffsetValue
import org.maplibre.compose.expressions.value.DpValue
import org.maplibre.compose.expressions.value.FloatValue
import org.maplibre.compose.expressions.value.TranslateAnchor
import org.maplibre.compose.sources.VectorSource
import org.maplibre.compose.style.TransitionOptions
import org.maplibre.compose.util.MaplibreComposable

/**
 * A circle layer draws points from the [sourceLayer] in the given [source] in the given style as a
 * circles. If nothing else is specified, these will be black dots of 5 dp radius.
 *
 * @param id Unique layer name.
 * @param source Vector data source for this layer.
 * @param sourceLayer Layer to use from the given vector tile [source].
 * @param minZoom The minimum zoom level for the layer. At zoom levels less than this, the layer
 *   will be hidden. A value in the range of `[0..24]`.
 * @param maxZoom The maximum zoom level for the layer. At zoom levels equal to or greater than
 *   this, the layer will be hidden. A value in the range of `[0..24]`.
 * @param filter An expression specifying conditions on source features. Only features that match
 *   the filter are displayed. Zoom expressions in filters are only evaluated at integer zoom
 *   levels. The expression may use feature properties. The
 *   [feature state][org.maplibre.compose.expressions.dsl.Feature.state] expression is not
 *   supported.
 * @param visible Whether the layer should be displayed.
 * @param sortKey Sorts features within this layer in ascending order based on this value. Features
 *   with a higher sort key will appear above features with a lower sort key. The expression may use
 *   feature properties.
 * @param translate The geometry's offset relative to the [translateAnchor]. Negative numbers
 *   indicate left and up, respectively.
 * @param translateTransition Timing for changes to [translate]. Null uses the style's global
 *   transition.
 * @param translateAnchor Frame of reference for offsetting geometry.
 *
 *   Ignored if [translate] is not set.
 *
 * @param opacity Circles opacity. A value in range `[0..1]`. The expression may use feature
 *   properties and feature state.
 * @param opacityTransition Timing for changes to [opacity]. Null uses the style's global
 *   transition.
 * @param color Circles fill color. The expression may use feature properties and feature state.
 * @param colorTransition Timing for changes to [color]. Null uses the style's global transition.
 * @param blur Amount to blur the circle. A value of `1` blurs the circle such that only the
 *   centerpoint has full opacity. The expression may use feature properties and feature state.
 * @param blurTransition Timing for changes to [blur]. Null uses the style's global transition.
 * @param radius Circles radius. The expression may use feature properties and feature state.
 * @param radiusTransition Timing for changes to [radius]. Null uses the style's global transition.
 * @param strokeOpacity Opacity of the circles' stroke. The expression may use feature properties
 *   and feature state.
 * @param strokeOpacityTransition Timing for changes to [strokeOpacity]. Null uses the style's
 *   global transition.
 * @param strokeColor Circles' stroke color. The expression may use feature properties and feature
 *   state.
 * @param strokeColorTransition Timing for changes to [strokeColor]. Null uses the style's global
 *   transition.
 * @param strokeWidth Thickness of the circles' stroke. Strokes are placed outside of the [radius].
 *   The expression may use feature properties and feature state.
 * @param strokeWidthTransition Timing for changes to [strokeWidth]. Null uses the style's global
 *   transition.
 * @param pitchScale Scaling behavior of circles when the map is pitched.
 * @param pitchAlignment Orientation of circles when the map is pitched.
 * @param onClick Function to call when any feature in this layer has been clicked.
 * @param onLongClick Called for a touch long press or secondary mouse click on this layer.
 * @param onDoubleClick Called for a double tap or double click on this layer.
 * @param hitPadding Expands tap queries to a square of this radius in dp; zero uses a point.
 */
@Composable
@MaplibreComposable
public fun CircleLayer(
  id: String,
  source: VectorSource,
  sourceLayer: String = "",
  minZoom: Float = 0.0f,
  maxZoom: Float = 24.0f,
  filter: Expression<BooleanValue>? = null,
  visible: Boolean = true,
  sortKey: Expression<FloatValue>? = null,
  translate: Expression<DpOffsetValue> = const(DpOffset.Zero),
  translateTransition: TransitionOptions? = null,
  translateAnchor: Expression<TranslateAnchor> = const(TranslateAnchor.Map),
  opacity: Expression<FloatValue> = const(1f),
  opacityTransition: TransitionOptions? = null,
  color: Expression<ColorValue> = const(Color.Black),
  colorTransition: TransitionOptions? = null,
  blur: Expression<FloatValue> = const(0f),
  blurTransition: TransitionOptions? = null,
  radius: Expression<DpValue> = const(5.dp),
  radiusTransition: TransitionOptions? = null,
  strokeOpacity: Expression<FloatValue> = const(1f),
  strokeOpacityTransition: TransitionOptions? = null,
  strokeColor: Expression<ColorValue> = const(Color.Black),
  strokeColorTransition: TransitionOptions? = null,
  strokeWidth: Expression<DpValue> = const(0.dp),
  strokeWidthTransition: TransitionOptions? = null,
  pitchScale: Expression<CirclePitchScale> = const(CirclePitchScale.Map),
  pitchAlignment: Expression<CirclePitchAlignment> = const(CirclePitchAlignment.Viewport),
  onClick: FeaturesClickHandler? = null,
  onLongClick: FeaturesClickHandler? = null,
  onDoubleClick: FeaturesClickHandler? = null,
  hitPadding: Dp = 0.dp,
) {

  Layer(
    id = id,
    source = source,
    type = "circle",
    filterUnsupportedProperties = true,
    onClick = onClick,
    onLongClick = onLongClick,
    onDoubleClick = onDoubleClick,
    hitPadding = hitPadding,
  ) {
    root("source-layer", JsonPrimitive(sourceLayer))
    root("minzoom", JsonPrimitive(minZoom))
    root("maxzoom", JsonPrimitive(maxZoom))
    root("filter", filter)
    layout("visibility", JsonPrimitive(if (visible) "visible" else "none"))
    layout("circle-sort-key", sortKey)
    paint("circle-radius", radius)
    paintTransition("circle-radius", radiusTransition)
    paint("circle-color", color)
    paintTransition("circle-color", colorTransition)
    paint("circle-blur", blur)
    paintTransition("circle-blur", blurTransition)
    paint("circle-opacity", opacity)
    paintTransition("circle-opacity", opacityTransition)
    paint("circle-translate", translate)
    paintTransition("circle-translate", translateTransition)
    paint("circle-translate-anchor", translateAnchor)
    paint("circle-pitch-scale", pitchScale)
    paint("circle-pitch-alignment", pitchAlignment)
    paint("circle-stroke-width", strokeWidth)
    paintTransition("circle-stroke-width", strokeWidthTransition)
    paint("circle-stroke-color", strokeColor)
    paintTransition("circle-stroke-color", strokeColorTransition)
    paint("circle-stroke-opacity", strokeOpacity)
    paintTransition("circle-stroke-opacity", strokeOpacityTransition)
  }
}
